import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Award, Share2, Download, Linkedin, Copy, Check, ExternalLink, Lock } from 'lucide-react';
import Layout from '@/components/Layout';
import StrataMap from '@/components/StrataMap';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Share() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [badge, setBadge] = useState(null);
  const [signatureCard, setSignatureCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [copied, setCopied] = useState(false);
  const [proofBadgeOpen, setProofBadgeOpen] = useState(false);
  const [proofForm, setProofForm] = useState({ variant: 'interview_ready', role_title: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, badgeRes, cardRes] = await Promise.all([
        fetch(`${API_URL}/api/dashboard/stats`, { credentials: 'include' }),
        fetch(`${API_URL}/api/sharing/badge`, { credentials: 'include' }),
        fetch(`${API_URL}/api/sharing/signature-card`, { credentials: 'include' }).catch(() => null)
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (badgeRes.ok) setBadge(await badgeRes.json());
      if (cardRes?.ok) setSignatureCard(await cardRes.json());
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const claimBadge = async () => {
    setClaiming(true);
    try {
      const response = await fetch(`${API_URL}/api/sharing/claim-badge`, {
        method: 'POST',
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to claim badge');

      const data = await response.json();
      setBadge({ ...badge, badge: data });
      toast.success('Strata Record Badge claimed successfully!');
    } catch (error) {
      toast.error('Failed to claim badge');
    } finally {
      setClaiming(false);
    }
  };

  const claimProofBadge = async () => {
    setClaiming(true);
    try {
      const response = await fetch(`${API_URL}/api/sharing/proof-badge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(proofForm)
      });

      if (!response.ok) throw new Error('Failed to claim badge');

      toast.success('Proof Badge claimed successfully!');
      setProofBadgeOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to claim badge');
    } finally {
      setClaiming(false);
    }
  };

  const copyVerificationUrl = () => {
    const url = `${window.location.origin}/verify/${badge?.badge?.verification_code}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success('Verification URL copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const shareToLinkedIn = (type) => {
    let url = '';
    let text = '';
    
    if (type === 'badge') {
      url = `${window.location.origin}/verify/${badge?.badge?.verification_code}`;
      text = `I've built my Strata Record — a verified, multi-source leadership profile built from ${badge?.badge?.source_count} assessment sources spanning ${badge?.badge?.year_range_start}–${badge?.badge?.year_range_end}. #Leadership #StrataRecord`;
    } else if (type === 'signature') {
      text = signatureCard?.suggested_caption || '';
      url = 'https://strata.com';
    }
    
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}&summary=${encodeURIComponent(text)}`;
    window.open(linkedInUrl, '_blank', 'width=600,height=600');
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <div className="w-12 h-12 border-4 border-amber-600/30 border-t-amber-600 rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 max-w-5xl" data-testid="share-page">
        {/* Header */}
        <div>
          <h1 className="font-display text-3xl text-white mb-2">Share Your Strata Record</h1>
          <p className="text-slate-400">
            Share your leadership credentials with your network. Every share is a signal of professional commitment.
          </p>
        </div>

        {/* Asset 1: Strata Record Badge */}
        <Card className="strata-card-accent">
          <CardHeader>
            <CardTitle className="font-display text-xl text-white flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-600/20 flex items-center justify-center">
                <Award className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <span>Strata Record Badge</span>
                <p className="text-sm font-normal text-slate-400 mt-0.5">Free at 3+ sources</p>
              </div>
              {badge?.badge && (
                <span className="ml-auto px-3 py-1 rounded-full bg-green-500/20 text-green-400 text-sm">
                  Claimed
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Badge Preview */}
              <div className="flex justify-center">
                <div className="relative p-8 rounded-2xl border-2 border-slate-700" style={{ backgroundColor: '#0D0F14' }}>
                  {/* Badge Visual */}
                  <div className="text-center space-y-4">
                    <img 
                      src="/strata-lotus-mark.svg" 
                      alt="Strata Record Lotus" 
                      className="w-24 h-24 mx-auto"
                    />
                    <div>
                      <p className="text-amber-500 text-xs uppercase tracking-wider font-medium mb-1">Verified Credential</p>
                      <h3 className="font-display text-xl text-white">Strata Record</h3>
                    </div>
                    {badge?.badge ? (
                      <div className="space-y-2 pt-2">
                        <p className="text-slate-300 text-sm">
                          Built from <span className="text-white font-medium">{badge.badge.source_count}</span> assessment sources
                        </p>
                        <p className="text-slate-400 text-sm">
                          Spanning {badge.badge.year_range_start} — {badge.badge.year_range_end}
                        </p>
                        <p className="font-mono text-xs text-slate-500 pt-2">
                          {badge.badge.verification_code}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2 pt-2 opacity-50">
                        <p className="text-slate-400 text-sm">Built from [N] assessment sources</p>
                        <p className="text-slate-500 text-sm">Spanning [year] — [year]</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Badge Actions */}
              <div className="space-y-6">
                <div>
                  <h4 className="text-white font-medium mb-2">What it signals</h4>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    "I have a verified, multi-source leadership record." The badge confirms you've consolidated multiple 
                    assessments without exposing scores or details — privacy by design.
                  </p>
                </div>

                {badge?.eligible ? (
                  badge?.badge ? (
                    <div className="space-y-3">
                      <div className="flex gap-3">
                        <Button 
                          onClick={() => shareToLinkedIn('badge')}
                          className="flex-1 bg-[#0A66C2] hover:bg-[#004182] text-white"
                        >
                          <Linkedin className="w-4 h-4 mr-2" />
                          Share to LinkedIn
                        </Button>
                        <Button 
                          variant="outline" 
                          onClick={copyVerificationUrl}
                          className="strata-button-secondary"
                        >
                          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        </Button>
                      </div>
                      <a 
                        href={`/verify/${badge.badge.verification_code}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-amber-500 text-sm hover:text-amber-400"
                      >
                        <ExternalLink className="w-4 h-4" />
                        View public verification page
                      </a>
                    </div>
                  ) : (
                    <Button 
                      onClick={claimBadge}
                      disabled={claiming}
                      className="w-full strata-button-primary"
                      data-testid="claim-badge-btn"
                    >
                      {claiming ? 'Claiming...' : 'Claim Your Strata Record Badge'}
                    </Button>
                  )
                ) : (
                  <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700">
                    <div className="flex items-center gap-3 text-slate-400">
                      <Lock className="w-5 h-5" />
                      <div>
                        <p className="text-white text-sm font-medium">
                          {3 - (badge?.source_count || 0)} more sources needed
                        </p>
                        <p className="text-xs">Add processed sources to unlock your badge</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Asset 2: Signature Card */}
        <Card className={`strata-card ${signatureCard ? '' : 'opacity-75'}`}>
          <CardHeader>
            <CardTitle className="font-display text-xl text-white flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-state-connect/20 flex items-center justify-center">
                <Share2 className="w-5 h-5 text-[#D88BC4]" />
              </div>
              <div>
                <span>Strata Record Signature Card</span>
                <p className="text-sm font-normal text-slate-400 mt-0.5">Strata Record Synthesis purchasers only</p>
              </div>
              {!signatureCard && (
                <span className="ml-auto px-3 py-1 rounded-full bg-slate-700 text-slate-400 text-sm">
                  <Lock className="w-3 h-3 inline mr-1" />
                  Locked
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {signatureCard ? (
              <div className="space-y-6">
                {/* Card Preview */}
                <div 
                  className="rounded-xl overflow-hidden border border-slate-700"
                  style={{ backgroundColor: '#0D0F14' }}
                >
                  <div className="p-8 flex gap-8 items-center" style={{ aspectRatio: '1200/628' }}>
                    {/* Lotus Map */}
                    <div className="shrink-0">
                      <StrataMap 
                        scores={signatureCard.state_scores} 
                        size={200} 
                        animated={false}
                        showLabels={false}
                      />
                    </div>
                    
                    {/* Signature */}
                    <div className="flex-1 border-l-2 border-amber-600/30 pl-8">
                      <p className="font-display text-lg lg:text-xl text-white leading-relaxed italic">
                        "{signatureCard.strata_signature}"
                      </p>
                      <div className="mt-4 flex gap-4 text-sm text-slate-400">
                        {Object.entries(signatureCard.state_scores || {}).map(([key, val]) => (
                          <span key={key} className="capitalize">{key} {val}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="px-8 py-3 border-t border-slate-800 flex justify-between items-center">
                    <span className="text-slate-500 text-sm uppercase tracking-wider">My Strata Record</span>
                    <span className="text-amber-500 text-sm">strata.com</span>
                  </div>
                </div>

                {/* Caption & Actions */}
                <div>
                  <label className="text-slate-400 text-sm mb-2 block">Suggested LinkedIn caption (edit as you wish)</label>
                  <Textarea 
                    value={signatureCard.suggested_caption}
                    className="strata-input h-24 text-sm"
                    readOnly
                  />
                </div>

                <div className="flex gap-3">
                  <Button 
                    onClick={() => shareToLinkedIn('signature')}
                    className="flex-1 bg-[#0A66C2] hover:bg-[#004182] text-white"
                  >
                    <Linkedin className="w-4 h-4 mr-2" />
                    Share to LinkedIn
                  </Button>
                  <Button variant="outline" className="strata-button-secondary">
                    <Download className="w-4 h-4 mr-2" />
                    Download PNG
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-slate-400 mb-4">
                  Complete your Strata Record Synthesis to unlock your personalized Signature Card
                </p>
                <Button 
                  onClick={() => navigate('/insights')}
                  variant="outline"
                  className="strata-button-secondary"
                >
                  Go to Insights
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Asset 3: Proof Badge */}
        <Card className={`strata-card ${stats?.has_proof ? '' : 'opacity-75'}`}>
          <CardHeader>
            <CardTitle className="font-display text-xl text-white flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-state-execute/20 flex items-center justify-center">
                <Award className="w-5 h-5 text-[#F08050]" />
              </div>
              <div>
                <span>Proof Completion Badge</span>
                <p className="text-sm font-normal text-slate-400 mt-0.5">Proof purchasers only</p>
              </div>
              {!stats?.has_proof && (
                <span className="ml-auto px-3 py-1 rounded-full bg-slate-700 text-slate-400 text-sm">
                  <Lock className="w-3 h-3 inline mr-1" />
                  Locked
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.has_proof ? (
              <div className="grid lg:grid-cols-2 gap-8">
                <div>
                  <h4 className="text-white font-medium mb-2">Interview Ready or Succession Prepared</h4>
                  <p className="text-slate-400 text-sm leading-relaxed mb-4">
                    Signal systematic, evidence-based preparation for your next opportunity. 
                    This badge confirms you've completed structured interview or succession preparation 
                    using your longitudinal leadership record.
                  </p>
                  <p className="text-slate-500 text-sm">
                    <strong>Privacy note:</strong> The badge shows only the role type, not the company or specific position.
                  </p>
                </div>

                <div className="flex items-center justify-center">
                  <Dialog open={proofBadgeOpen} onOpenChange={setProofBadgeOpen}>
                    <DialogTrigger asChild>
                      <Button className="strata-button-primary">
                        Claim Proof Badge
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-slate border-slate-mid" style={{ backgroundColor: '#1C2130' }}>
                      <DialogHeader>
                        <DialogTitle className="font-display text-xl text-white">Claim Proof Badge</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 pt-4">
                        <div>
                          <label className="text-slate-300 text-sm mb-2 block">Badge Type</label>
                          <Select
                            value={proofForm.variant}
                            onValueChange={(v) => setProofForm(prev => ({ ...prev, variant: v }))}
                          >
                            <SelectTrigger className="strata-input">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="interview_ready">Interview Ready</SelectItem>
                              <SelectItem value="succession_prepared">Succession Prepared</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="text-slate-300 text-sm mb-2 block">Role Title (generic)</label>
                          <Input
                            value={proofForm.role_title}
                            onChange={(e) => setProofForm(prev => ({ ...prev, role_title: e.target.value }))}
                            placeholder="e.g., Chief Product Officer"
                            className="strata-input"
                          />
                        </div>
                        <Button 
                          onClick={claimProofBadge}
                          disabled={claiming}
                          className="w-full strata-button-primary"
                        >
                          {claiming ? 'Claiming...' : 'Claim Badge'}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-slate-400 mb-4">
                  Complete your Proof document to unlock this credential
                </p>
                <Button 
                  onClick={() => navigate('/proof')}
                  variant="outline"
                  className="strata-button-secondary"
                >
                  Go to Proof
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
